import { useContext, useState } from "react";

import DisplayComments from "../../components/DisplayComments/DisplayComments";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  ButtonGroup,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material/";
import { ExpandMore, ListAlt } from "@mui/icons-material";
import { sections, useEventComments } from "../../hooks/useEventComments";
import { UserContext } from "../../UserContext";

const views = {
  all: "All",
  open: "Open Only",
  payments: "Payment Only",
};

const CommentsContainer = ({ event }) => {
  const [view, setView] = useState("all");
  const { id: eventId } = event;
  const { comments, isLoading, useToggleCommentDone, useReplyToComment } =
    useEventComments(eventId);
  const { featureFlags } = useContext(UserContext);
  const commentsFilter =
    view === "open"
      ? (comment) => !comment.isArchived || comment?.parentThreadId?.length > 0
      : (comment) =>
          comment?.sectionId === sections.financial.id &&
          comment?.fieldId === sections.financial.paymentFieldId;
  const localData = comments
    .map((section) => {
      const filteredComments =
        view === "all"
          ? section.comments
          : section.comments.filter(commentsFilter);

      if (!section?.menus?.length) {
        return {
          ...section,
          comments: filteredComments,
        };
      }
      return {
        ...section,
        menus: section.menus.filter((menu) =>
          filteredComments.some((fc) => fc.fieldId === menu.id)
        ),
        comments: filteredComments,
      };
    })
    .filter((o) => o.comments?.length > 0)
    .filter((l) =>
      l?.comments?.some(
        (c) =>
          !c?.isCatererReply &&
          (view === "open" ? !c?.parentThreadId?.length : 1)
      )
    );

  const handleFilter = (e) => {
    setView(e?.target?.value);
  };

  if (isLoading) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100vh" }}
      >
        <CircularProgress></CircularProgress>
      </Stack>
    );
  }
  return (
    <>
      <Stack direction="row" justifyContent="end" m={1} mr={2}>
        <ButtonGroup
          sx={{
            "& .MuiButtonGroup-grouped:first-of-type": {
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
            },
            "& .MuiButtonGroup-grouped:last-of-type": {
              borderTopRightRadius: 16,
              borderBottomRightRadius: 16,
            },
          }}
        >
          {Object.entries(views).map(([k, p]) => (
            <Button
              key={k}
              variant={view === k ? "contained" : "outlined"}
              onClick={handleFilter}
              value={k}
            >
              {p}
            </Button>
          ))}
        </ButtonGroup>
      </Stack>
      <Stack gap={1} m={2}>
        {localData?.length === 0 && (
          <Stack justifyContent="center" alignItems="center">
            <Typography variant="h5">No comments.</Typography>
          </Stack>
        )}
        {localData.map(({ id, title, type, icon, comments, menus }, idx) => (
          <Accordion key={id}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                "& .MuiAccordionSummary-content": {
                  justifyContent: "space-between",
                  marginRight: 2,
                },
              }}
            >
              <Stack direction="row" gap={1} flex={1}>
                {icon && <img src={icon} alt={title} />}
                <Typography>{title}</Typography>
              </Stack>
              <Badge
                badgeContent={
                  comments.filter((c) => !c.isArchived && !c?.isCatererReply)
                    .length || 0
                }
                color="secondary"
              >
                <ListAlt />
              </Badge>
            </AccordionSummary>
            <AccordionDetails>
              {!menus?.length && (
                <DisplayComments
                  type={type}
                  comments={comments}
                  replyDisabled={!featureFlags?.mentions}
                  onDone={useToggleCommentDone}
                  onReply={useReplyToComment}
                />
              )}
              {menus?.length > 0 && (
                <Grid container>
                  {menus.map(({ name, id }) => (
                    <Grid item xs={12} mb={2} key={id}>
                      <Grid
                        container
                        justifyContent="space-between"
                        sx={{
                          borderRadius: "10px 10px 0px 0px",
                          background: "rgba(0, 145, 255, 0.2)",
                          padding: 1.5,
                        }}
                      >
                        <Grid item>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              lineHeight: "1.6",
                              fontSize: "1rem",
                            }}
                          >
                            {name}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          border: "1px solid #F6F6F9",
                          borderRadius: "0px 0px 10px 10px",
                          background: "#F6F6F9",
                        }}
                      >
                        <DisplayComments
                          type={type}
                          comments={comments.filter((c) => c.fieldId === id)}
                          replyDisabled={!featureFlags?.mentions}
                          onDone={useToggleCommentDone}
                          onReply={useReplyToComment}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </>
  );
};

export default CommentsContainer;
